import React, { useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { Flex, Tag, Popover } from '@decisiv/ui-components';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import iconix from '@decisiv/iconix';
import tcs from 'polished/lib/color/toColorString';
import spacing from '@decisiv/design-tokens/lib/spacing';
import color from '@decisiv/design-tokens/lib/color';
import assignSsoProvider from '../services/ssoAssignmentService';

const StatusOption = styled(Flex)`
  padding: ${spacing.base * 0.5}px ${spacing.base}px;
  color: ${tcs(color.interaction.pacificOcean)};
  min-width: 180px;
  margin-bottom: ${spacing.base * 0.3}px;
  cursor: pointer;
  &:hover {
    color: ${tcs(color.base.snowWhite)};
    background-color: ${tcs(color.interaction.pacificOcean)};
  }
`;

export default function SsoAccessCell({
  rowData,
  availableSsoProviders,
  onSsoAssignmentChange,
}) {
  const [popover, setPopover] = useState({});
  const [isAssigning, setIsAssigning] = useState(false);
  const actionRef = useRef();
  const popoverRef = useCallback(setPopover, [popover]);

  const onOptionClick = async (provider) => {
    if (isAssigning) return;

    popover.hide();
    setIsAssigning(true);

    try {
      const result = await assignSsoProvider(rowData.id, provider);

      // Call the callback to update the parent component's state
      if (onSsoAssignmentChange) {
        onSsoAssignmentChange(rowData.id, {
          sso_access: result.sso_access,
        });
      }
    } finally {
      setIsAssigning(false);
    }
  };

  return isEmpty(availableSsoProviders?.providers) ||
    (rowData.sso_access && rowData.sso_access.assignment === 'domain') ? (
    <Tag
      color={rowData.sso_access ? 'koolaidCake' : 'warning'}
      text={rowData.sso_access ? rowData.sso_access.provider : 'Unassigned'}
    />
  ) : (
    <>
      <Tag
        action={() => popover.toggle()}
        actionRef={actionRef}
        actionIcon={popover.isVisible ? iconix.ChevronUp : iconix.ChevronDown}
        actionAttributes={{ 'aria-label': 'Expand' }}
        color={rowData.sso_access ? 'koolaidCake' : 'warning'}
        text={
          isAssigning
            ? 'Updating...'
            : rowData.sso_access?.provider || 'Unassigned'
        }
        disabled={isAssigning}
      />

      <Popover ref={popoverRef} target={actionRef} manageEvents={false}>
        <Flex flexDirection="column">
          {!availableSsoProviders.require_sso && (
            <StatusOption
              key="unassigned"
              onClick={() => {
                if (!isAssigning && rowData.sso_access?.provider) {
                  onOptionClick('unassigned');
                }
              }}
              style={{
                opacity: isAssigning || !rowData.sso_access?.provider ? 0.5 : 1,
                cursor:
                  isAssigning || !rowData.sso_access?.provider
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              Unassigned
            </StatusOption>
          )}
          {availableSsoProviders.providers?.map((provider) => {
            const isCurrentProvider = rowData.sso_access?.provider === provider;
            return (
              <StatusOption
                key={provider}
                onClick={() => {
                  if (!isAssigning && !isCurrentProvider) {
                    onOptionClick(provider);
                  }
                }}
                style={{
                  opacity: isAssigning || isCurrentProvider ? 0.5 : 1,
                  cursor:
                    isAssigning || isCurrentProvider
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {provider}
              </StatusOption>
            );
          })}
        </Flex>
      </Popover>
    </>
  );
}

SsoAccessCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sso_access: PropTypes.shape({
      provider: PropTypes.string,
      assignment: PropTypes.string,
    }),
  }).isRequired,
  availableSsoProviders: PropTypes.shape({
    providers: PropTypes.arrayOf(PropTypes.string),
    require_sso: PropTypes.bool,
  }),
  onSsoAssignmentChange: PropTypes.func,
};

SsoAccessCell.defaultProps = {
  availableSsoProviders: {
    providers: [],
    require_sso: false,
  },
  onSsoAssignmentChange: null,
};
